import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useLocation} from '@github-ui/react-router'
import {isStaff} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useRef} from 'react'

import {useQueryEditContext} from '../contexts/QueryContext'

type SearchMetricState = {
  query: string
  timestamp: number
  searchId: string
}

/**
 * Hook to track search success metrics for issue searches.
 * Only tracks searches that include "is:issue" and have free-text content.
 *
 * Each qualifying search is assigned a unique ID that's included in both:
 * - The initial search event (search.performed_with_freetext)
 * - Any subsequent success events (search.successful_search_action)
 *
 * This allows linking searches to outcomes in analytics for correlation analysis.
 */
export function useSearchMetrics() {
  const {bulkJobId} = useQueryEditContext()
  const {sendAnalyticsEvent} = useAnalytics()
  const {search: locationSearch} = useLocation()

  // Track the last search that was explicitly submitted with free-text
  const lastSearchRef = useRef<SearchMetricState | null>(null)

  // Track previous URL query to detect changes
  const prevUrlQueryRef = useRef<string | null>(null)

  // Check if a query is an issue search with free-text
  const isIssueSearchWithFreeText = useCallback((query: string): boolean => {
    if (!query || query.trim() === '') return false

    // Only track searches that explicitly include "is:issue"
    if (!query.match(/\bis:issue\b/i)) return false

    // Remove common qualifiers to see if there's free-text remaining
    // Pattern matches qualifier:value where value can be either:
    // - A quoted string: "value with spaces"
    // - A non-whitespace sequence: value
    const qualifierPattern =
      /\b(is|type|state|assignee|author|label|milestone|project|repo|org|user|mentions|team|commenter|involves|reviewed-by|review-requested|archived|no|sort|order|created|updated|closed|merged|comments|reactions|interactions|in|language|status):("((?:[^"\\]|\\.)*)"|[^\s]+)/gi

    const withoutQualifiers = query.replace(qualifierPattern, '').trim()

    return withoutQualifiers.length > 0
  }, [])

  const trackSearchSubmit = useCallback(
    (query: string) => {
      const isTrackable = isIssueSearchWithFreeText(query)

      // If this is not a trackable search, clear any pending search and return
      if (!isTrackable) {
        lastSearchRef.current = null
        return
      }

      // If this is the same query as the last one, don't track it again
      if (lastSearchRef.current && lastSearchRef.current.query === query) {
        return
      }

      // Generate a unique ID for this search
      const searchId = crypto.randomUUID()

      lastSearchRef.current = {
        query,
        timestamp: Date.now(),
        searchId,
      }

      sendAnalyticsEvent('search.performed_with_freetext', 'SEARCH_METRICS', {
        query,
        search_id: searchId,
        semantic_search_enabled: isFeatureEnabled('issues_index_semantic_search'),
        is_staff: isStaff(),
      })
    },
    [isIssueSearchWithFreeText, sendAnalyticsEvent],
  )

  // Track bulk action as success
  useEffect(() => {
    if (bulkJobId && lastSearchRef.current) {
      const timeSinceSearch = Date.now() - lastSearchRef.current.timestamp

      sendAnalyticsEvent('search.successful_search_action', 'SEARCH_METRICS', {
        query: lastSearchRef.current.query,
        search_id: lastSearchRef.current.searchId,
        action: 'bulk_action',
        time_to_action_ms: timeSinceSearch,
        semantic_search_enabled: isFeatureEnabled('issues_index_semantic_search'),
        is_staff: isStaff(),
      })

      // Clear the tracked search after success
      lastSearchRef.current = null
    }
  }, [bulkJobId, sendAnalyticsEvent])

  // Function to manually track issue navigation success
  const trackIssueNavigation = useCallback(
    (position?: number) => {
      if (lastSearchRef.current) {
        const timeSinceSearch = Date.now() - lastSearchRef.current.timestamp

        sendAnalyticsEvent('search.successful_search_action', 'SEARCH_METRICS', {
          query: lastSearchRef.current.query,
          search_id: lastSearchRef.current.searchId,
          action: 'issue_navigation',
          time_to_action_ms: timeSinceSearch,
          semantic_search_enabled: isFeatureEnabled('issues_index_semantic_search'),
          is_staff: isStaff(),
          ...(position !== undefined && {position: position + 1}),
        })

        // Clear the tracked search after success
        lastSearchRef.current = null
      }
    },
    [sendAnalyticsEvent],
  )

  // Track search when URL query changes (e.g., from picker selection)
  useEffect(() => {
    const urlQuery = new URLSearchParams(locationSearch).get('q')

    // Skip initial load - only track when query actually changes
    if (prevUrlQueryRef.current === null) {
      prevUrlQueryRef.current = urlQuery
      return
    }

    // Only track if query changed
    if (urlQuery && urlQuery !== prevUrlQueryRef.current) {
      trackSearchSubmit(urlQuery)
    }
    prevUrlQueryRef.current = urlQuery
  }, [locationSearch, trackSearchSubmit])

  return {
    trackIssueNavigation,
    // Exposed for testing - tracking is primarily done via URL changes
    trackSearchSubmit,
  }
}
